import React, { useState, useEffect, useCallback, useRef } from 'react';
import supportInnovationApi from '../services/support-innovation';
import supportInnovationDocsApi from '../services/support-innovation-documents';
import permissionsApi from '../services/permissions';
import { PAGES } from './Permissions';
import { DocumentSection } from './shared/DocumentViewer';
import { normalizeDoc, compressFile } from '../utils/documents';
import './SupportInnovation.css';

import { texteTheme } from '../utils/theme';
const PAGE_LABEL = Object.fromEntries(PAGES.map(p => [p.id, p.nom]));
const pageLabel = (id) => PAGE_LABEL[id] || id;

const ASSIGNEES = [
  { key: 'FA', label: 'François',   color: 'var(--color-accent)' },
  { key: 'PH', label: 'Paul-Henri', color: 'var(--color-decision)' },
];

const PRIORITES = [
  { value: '1', label: 'Confort',  color: 'var(--color-info)',    bg: 'var(--color-info-light)' },
  { value: '2', label: 'Critique', color: 'var(--color-warning)', bg: 'var(--color-warning-light)' },
  { value: '3', label: 'Bloquant', color: 'var(--color-error)',   bg: 'var(--color-error-light)' },
];

const TYPE_LABELS = {
  support:   { label: 'Bug',          short: 'Bug',          icon: '🐛', color: 'var(--color-error)',   bg: 'var(--color-error-light)' },
  evolution: { label: 'Amélioration', short: 'Amélioration', icon: '✨', color: 'var(--color-info)',    bg: 'var(--color-info-light)' },
};

const STATUTS = [
  { value: 'nouveau',     label: 'Nouveau',     color: 'var(--color-info)' },
  { value: 'en_cours',    label: 'En cours',    color: 'var(--color-warning)' },
  { value: 'en_attente',  label: 'En attente',  color: 'var(--color-paused)' },
  { value: 'planifie',    label: 'Planifié',    color: 'var(--color-accent)', hidden: true },
  { value: 'a_valider',   label: 'À valider',   color: 'var(--color-decision)' },
  { value: 'resolu',      label: 'Validé',      color: 'var(--color-success)' },
  { value: 'refuse',      label: 'Refusé',      color: 'var(--color-error)' },
];
const STATUTS_VISIBLE = STATUTS.filter(s => !s.hidden);

// Groupes d'affichage des sous-tickets (section "Sous-tickets" de la modale détail) :
// Les 4 premiers sont dépliés par défaut ; "clos" est replié par défaut.
const SUB_TICKET_GROUPS = [
  { key: 'nouveau',     label: 'Nouveau',   statuts: ['nouveau'],              collapsed: false },
  { key: 'en_cours',   label: 'En cours',   statuts: ['en_cours', 'planifie'], collapsed: false },
  { key: 'en_attente', label: 'En attente', statuts: ['en_attente'],           collapsed: false },
  { key: 'a_valider',  label: 'À valider',  statuts: ['a_valider'],            collapsed: false },
  { key: 'clos',       label: 'Clos',       statuts: ['resolu', 'refuse'],     collapsed: true  },
];
const statutInfo = (v) => STATUTS.find(s => s.value === v) || { label: v, color: 'var(--text-muted)' };

const initials = (prenom, nom) =>
  ((prenom || '').charAt(0) + (nom || '').charAt(0)).toUpperCase();

const ticketNum = (n) => n ? `#${String(n).padStart(5, '0')}` : '';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

// ─── Carte de ticket ─────────────────────────────────────────────────────────
function TicketCard({ d, byPage, isSuperAdmin, onOpen, onDelete, editMode, onDragStart, onDragEnd, isDragging }) {
  const si = statutInfo(d.statut);
  const type = TYPE_LABELS[d.type] || { label: d.type, icon: '•', color: 'var(--text-muted)', bg: 'var(--bg-muted)' };

  return (
    <div
      className={`si-card${isDragging ? ' is-dragging' : ''}${Number(d.nb_children) > 0 ? ' si-card--master' : ''}${d.parent_ticket_id ? ' si-card--child' : ''}`}
      draggable={!!onDragStart}
      onDragStart={onDragStart ? (e) => { e.stopPropagation(); onDragStart(d.id); } : undefined}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(d.id)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onOpen(d.id)}
    >
      <div className="si-card-top">
        <div className="si-card-badges">
          <span className="si-card-type-badge" style={{ color: type.color, background: type.bg }}>
            {type.icon} {type.label}
          </span>
          {d.priorite && (() => {
            const p = PRIORITES.find(x => x.value === String(d.priorite));
            return p ? (
              <span className="si-card-prio-badge" style={{ color: p.color, background: p.bg }}>
                P{d.priorite} {p.label}
              </span>
            ) : null;
          })()}
          {!byPage && d.page && (
            <span className="si-card-page-badge">{pageLabel(d.page)}</span>
          )}
          {byPage && (
            <span className="si-statut-pill" style={{ color: texteTheme(si.color), borderColor: si.color }}>
              {si.label}
            </span>
          )}
        </div>
        <span className="si-card-date">{formatDate(d.created_at)}</span>
      </div>

      <div className="si-card-title">
        {d.ticket_number && <span className="si-ticket-num">{ticketNum(d.ticket_number)}</span>}
        {d.titre}
        {d.modifier_prenom && (
          <span
            className="si-modifier-badge"
            title={`Modifié par ${d.modifier_prenom} ${d.modifier_nom}\nle ${formatDate(d.last_modified_at)}`}
          >
            {initials(d.modifier_prenom, d.modifier_nom)}
          </span>
        )}
      </div>

      <div className="si-card-footer">
        <div className="si-card-footer-left">
          {d.assigned_to && (d.assigned_to || '').split(',').filter(Boolean).map(key => {
            const a = ASSIGNEES.find(x => x.key === key);
            return a ? (
              <span key={key} className="si-assigned-badge" style={{ background: a.color }} title={a.label}>
                {a.key}
              </span>
            ) : null;
          })}
          {isSuperAdmin && d.auteur_prenom && (
            <span className="si-card-author">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              {d.auteur_prenom} {d.auteur_nom}
            </span>
          )}
          {d.date_livraison_prevue && (
            <span className="si-card-livraison">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              {formatDate(d.date_livraison_prevue)}
            </span>
          )}
        </div>
        <div className="si-card-footer-right">
          {Number(d.nb_commentaires) > 0 && (
            <span className="si-card-comments">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              {d.nb_commentaires}
            </span>
          )}
          {Number(d.nb_children) > 0 && (
            <div className="si-children-progress-wrap" title={`${d.nb_children_done}/${d.nb_children} sous-ticket(s) terminé(s)`}>
              <span className="si-children-count">🔗 {d.nb_children_done}/{d.nb_children}</span>
              <div className="si-children-bar">
                <div className="si-children-bar-fill" style={{ width: `${Math.round((Number(d.nb_children_done) / Number(d.nb_children)) * 100)}%` }} />
              </div>
            </div>
          )}
          {editMode && isSuperAdmin && (
            <button
              className="btn-icon delete"
              title="Supprimer"
              onClick={e => { e.stopPropagation(); onDelete(d.id); }}
            >🗑️</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function SupportInnovation({ user, setHeaderAction, editMode = false, initialTicketId = null }) {
  const isSuperAdmin = user?.role === 'super-admin';
  const isAdmin = user?.role === 'admin'; // peut voir/éditer titre-description/commenter tout ticket (pas les actions super-admin)

  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filterType, setFilterType]         = useState('');
  const [filterStatut, setFilterStatut]     = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterPriorite, setFilterPriorite] = useState('');
  const [search, setSearch]                 = useState('');
  const [dashData, setDashData]         = useState(null);

  // Vue liste/kanban toggle (dans l'onglet Kanban)
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'list'

  // Colonnes Kanban repliées (collapsed par défaut pour resolu + refuse)
  const [collapsedCols, setCollapsedCols] = useState(() => new Set(['resolu', 'refuse']));
  const toggleColCollapse = (val) => {
    setCollapsedCols(prev => {
      const next = new Set(prev);
      next.has(val) ? next.delete(val) : next.add(val);
      return next;
    });
  };

  // Drag & drop (kanban)
  const draggingId  = useRef(null);
  const [draggingCard, setDraggingCard] = useState(null);
  const [dragOverCol, setDragOverCol]   = useState(null);

  // Modale d'attribution (nouveau → en_cours)
  const [assignModal, setAssignModal] = useState(null); // { id, targetStatut }

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ type: 'support', titre: '', description: '', page: '', priorite: '', assigned_to: '' });
  const [newDocuments, setNewDocuments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [linkedContext, setLinkedContext] = useState(null); // ID du ticket maître lors d'une création liée

  const [accessiblePages, setAccessiblePages] = useState([]);
  useEffect(() => {
    permissionsApi.getMyPages()
      .then(r => {
        const ids = r.data.all ? PAGES.map(p => p.id) : (r.data.pages || []);
        const idSet = new Set(ids);
        setAccessiblePages(PAGES.filter(p => idSet.has(p.id)));
      })
      .catch(() => setAccessiblePages([]));
  }, []);

  const [detailId, setDetailId] = useState(initialTicketId);
  const [groupBy, setGroupBy] = useState('dashboard');
  const [promptModal, setPromptModal] = useState(null);

  // Ouvre un ticket et répercute son id dans l'URL (?page=support-innovation&ticket=<id>)
  // pour permettre de partager un lien direct. Pas de routeur dans ce projet : on
  // manipule l'historique directement, sans recharger la page.
  const openTicket = useCallback((id) => {
    setDetailId(id);
    const params = new URLSearchParams(window.location.search);
    params.set('page', 'support-innovation');
    params.set('ticket', String(id));
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, []);
  const closeTicket = useCallback(() => {
    setDetailId(null);
    const params = new URLSearchParams(window.location.search);
    params.delete('ticket');
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  }, []);

  const loadDemandes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType)   params.type   = filterType;
      if (filterStatut) params.statut = filterStatut;
      if (search)       params.search = search;
      const r = await supportInnovationApi.getAll(params);
      setDemandes(r.data.demandes || []);
    } catch (err) {
      console.error('[Support] chargement:', err);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatut, search]);

  useEffect(() => { loadDemandes(); }, [loadDemandes]);

  // Dashboard global counts (no filters)
  const loadDashData = useCallback(async () => {
    try {
      const r = await supportInnovationApi.getAll({});
      const all = r.data.demandes || [];
      const byStatut = STATUTS_VISIBLE.reduce((acc, s) => {
        acc[s.value] = all.filter(d => d.statut === s.value).length;
        return acc;
      }, {});
      const byType = {
        support:   all.filter(d => d.type === 'support').length,
        evolution: all.filter(d => d.type === 'evolution').length,
      };

      // ── KPIs (bornes de temps locales) ──
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const mondayOffset = (now.getDay() + 6) % 7; // lundi = 0
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfToday.getDate() - mondayOffset);
      const CLOS = ['resolu', 'refuse'];
      const kpis = {
        toValidate:        all.filter(d => d.statut === 'a_valider').length,
        inProgress:        all.filter(d => d.statut === 'en_cours').length,
        validatedThisWeek: all.filter(d => d.statut === 'resolu' && d.statut_changed_at && new Date(d.statut_changed_at) >= startOfWeek).length,
        blockingOpen:      all.filter(d => String(d.priorite) === '3' && !CLOS.includes(d.statut)).length,
      };

      setDashData({ total: all.length, byStatut, byType, kpis });
    } catch (_) {}
  }, []);

  useEffect(() => { loadDashData(); }, [loadDashData]);

  useEffect(() => {
    if (!setHeaderAction) return;
    setHeaderAction({
      primary: {
        label: '+ Nouvelle demande',
        onClick: () => {
          setForm({ type: 'support', titre: '', description: '', page: '', priorite: '', assigned_to: '' });
          setLinkedContext(null);
          setNewDocuments([]);
          setShowCreate(true);
        },
      },
    });
  }, [setHeaderAction]);

  const handleCreate = async () => {
    if (!form.titre.trim()) { alert('Le titre est requis'); return; }
    if (!form.priorite) { alert('La priorité est requise'); return; }
    setSaving(true);
    try {
      const createData = { ...form };
      if (linkedContext) createData.parent_ticket_id = linkedContext;
      const res = await supportInnovationApi.create(createData);
      const newId = res.data.demande?.id;
      if (linkedContext) setLinkedContext(null);
      const localDocs = (newDocuments || []).filter(d => d.isLocal && d.file);
      if (newId && localDocs.length > 0) {
        const failed = [];
        await Promise.all(localDocs.map(async (doc) => {
          try {
            if (doc.type === 'application/pdf') {
              const formData = new FormData();
              formData.append('file', doc.file, doc.name);
              await supportInnovationDocsApi.uploadFile(newId, formData);
            } else {
              const presignRes = await supportInnovationDocsApi.presign(newId, { filename: doc.name, contentType: doc.type });
              await fetch(presignRes.data.uploadUrl, { method: 'PUT', body: doc.file, headers: { 'Content-Type': doc.type } });
              await supportInnovationDocsApi.confirm(newId, { key: presignRes.data.key, filename: doc.name, contentType: doc.type, size: doc.file.size });
            }
          } catch (err) {
            console.error('Erreur upload document:', doc.name, err);
            failed.push(doc.name);
          }
        }));
        if (failed.length > 0) {
          alert(`La demande a été créée, mais ${failed.length} document(s) n'ont pas pu être joints :\n${failed.join('\n')}\n\nVous pouvez les rajouter depuis la demande.`);
        }
      }
      setShowCreate(false);
      setNewDocuments([]);
      setForm({ type: 'support', titre: '', description: '', page: '', priorite: '', assigned_to: '' });
      loadDemandes();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement cette demande ?')) return;
    try {
      await supportInnovationApi.remove(id);
      loadDemandes();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const buildGroupPrompt = (block) => {
    const byPage = groupBy === 'page';
    const list = block.list || [];
    const cible = byPage ? `la page « ${block.label} » du CRM` : `le statut « ${block.label} »`;
    const slug = (byPage && block.key !== '__none__') ? `\nSlug de page CRM : ${block.key}` : '';
    const intro =
      `Voici ${list.length} demande(s) Support concernant ${cible} (CRM Atelier Pro).${slug}\n` +
      `Merci de les traiter en respectant les conventions du projet (CLAUDE.md, design system à base de tokens CSS, mode édition/suppression super-admin, et mise à jour de la doc KNOWLEDGE/README/RGPD/SECURITY si pertinent).`;
    const body = list.map((d, i) => {
      const meta = [`Type : ${(TYPE_LABELS[d.type] || {}).short || d.type}`];
      if (!byPage && d.page) meta.push(`Page : ${pageLabel(d.page)} (slug: ${d.page})`);
      if (byPage) meta.push(`Statut : ${statutInfo(d.statut).label}`);
      if (d.priorite) meta.push(`Priorité : ${d.priorite}`);
      const desc = (d.description || '').trim() || '(pas de description)';
      return `### Demande ${i + 1} — ${d.titre}\n${meta.join(' · ')}\n\n${desc}`;
    }).join('\n\n---\n\n');
    return `${intro}\n\n${body}\n`;
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
  };

  const openGroupPrompt = async (block) => {
    if (promptModal && promptModal.loading) return;
    setPromptModal({ label: block.label, loading: true, text: '', fallback: false, copied: false });
    let text = '';
    let fallback = false;
    try {
      const res = await supportInnovationApi.groupPrompt({
        ids: (block.list || []).map(d => d.id),
        group_type: groupBy,
        group_label: block.label,
        page_slug: (groupBy === 'page' && block.key !== '__none__') ? block.key : '',
      });
      text = (res.data && res.data.prompt) || '';
    } catch {
      fallback = true;
    }
    if (!text) { text = buildGroupPrompt(block); fallback = true; }
    await copyToClipboard(text);
    setPromptModal({ label: block.label, loading: false, text, fallback, copied: true });
  };

  const copyModalText = async () => {
    if (!promptModal?.text) return;
    await copyToClipboard(promptModal.text);
    setPromptModal(m => (m ? { ...m, copied: true } : m));
  };

  // ── Drag & drop handlers
  const handleDragStart = useCallback((id) => {
    draggingId.current = id;
    setDraggingCard(id);
  }, []);

  const handleDragEnd = useCallback(() => {
    draggingId.current = null;
    setDraggingCard(null);
    setDragOverCol(null);
  }, []);

  const handleDrop = useCallback((targetStatut) => {
    const id = draggingId.current;
    draggingId.current = null;
    setDraggingCard(null);
    setDragOverCol(null);
    if (!id) return;
    const card = demandes.find(d => d.id === id);
    if (!card || card.statut === targetStatut) return;
    // Passage nouveau → en_cours : demander attribution
    if (targetStatut === 'en_cours' && card.statut === 'nouveau') {
      setAssignModal({ id, targetStatut });
      return;
    }
    applyDrop(id, targetStatut, card, {});
  }, [demandes]); // eslint-disable-line

  const applyDrop = useCallback(async (id, targetStatut, card, extra) => {
    setDemandes(prev => prev.map(d => d.id === id ? { ...d, statut: targetStatut, ...extra } : d));
    try {
      await supportInnovationApi.update(id, { statut: targetStatut, ...extra });
      loadDashData();
    } catch (err) {
      console.error('[Support] drag update:', err);
      setDemandes(prev => prev.map(d => d.id === id ? { ...d, statut: card.statut, assigned_to: card.assigned_to } : d));
    }
  }, [loadDashData]);

  const handleAssign = useCallback((assigneeKey) => {
    const { id, targetStatut } = assignModal;
    setAssignModal(null);
    const card = demandes.find(d => d.id === id);
    if (!card) return;
    applyDrop(id, targetStatut, card, { assigned_to: assigneeKey });
  }, [assignModal, demandes, applyDrop]);

  // ── Groupes de tickets
  const filteredDemandes = demandes.filter(d => {
    if (filterAssignee && !(d.assigned_to || '').split(',').includes(filterAssignee)) return false;
    if (filterPriorite && String(d.priorite) !== filterPriorite) return false;
    return true;
  });

  const computeBlocks = () => {
    if (!filteredDemandes.length) return [];
    const byPage = groupBy === 'page';
    const groups = {};
    if (byPage) {
      filteredDemandes.forEach(d => { const k = d.page || '__none__'; (groups[k] = groups[k] || []).push(d); });
      const ordrePages = PAGES.map(p => p.id).filter(id => groups[id]);
      const autres = Object.keys(groups).filter(k => k !== '__none__' && !ordrePages.includes(k));
      const keys = [...ordrePages, ...autres, ...(groups['__none__'] ? ['__none__'] : [])];
      return keys.map(k => ({
        key: k,
        label: k === '__none__' ? 'Page non précisée' : pageLabel(k),
        color: 'var(--color-accent)',
        list: groups[k],
      }));
    } else {
      filteredDemandes.forEach(d => { (groups[d.statut] = groups[d.statut] || []).push(d); });
      const ordre = STATUTS.map(s => s.value);
      const keys = [...ordre, ...Object.keys(groups).filter(k => !ordre.includes(k))];
      return keys.filter(k => groups[k]?.length).map(k => {
        const si = statutInfo(k);
        return { key: k, label: si.label, color: si.color, list: groups[k] };
      });
    }
  };
  const blocks = computeBlocks();

  return (
    <div className="page-container">

      {/* Onglets */}
      <div className="page-tabs">
        <button className={groupBy === 'dashboard' ? 'active' : ''} onClick={() => setGroupBy('dashboard')}>📊 Dashboard</button>
        <button className={groupBy === 'statut' ? 'active' : ''} onClick={() => setGroupBy('statut')}>📋 Kanban</button>
        <button className={groupBy === 'page' ? 'active' : ''} onClick={() => setGroupBy('page')}>📄 Par page</button>
      </div>

      {/* Toolbar — masquée sur le Dashboard */}
      {groupBy !== 'dashboard' && <div className="page-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className={`page-select${filterType ? ' active-filter' : ''}`} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">Tous les types</option>
          <option value="support">🐛 Bug</option>
          <option value="evolution">✨ Amélioration</option>
        </select>
        <select className={`page-select${filterStatut ? ' active-filter' : ''}`} value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
          <option value="">Tous les statuts</option>
          {STATUTS_VISIBLE.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select className={`page-select${filterAssignee ? ' active-filter' : ''}`} value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
          <option value="">Tous les assignés</option>
          {ASSIGNEES.map(a => <option key={a.key} value={a.key}>{a.key} — {a.label}</option>)}
        </select>
        <select className={`page-select${filterPriorite ? ' active-filter' : ''}`} value={filterPriorite} onChange={e => setFilterPriorite(e.target.value)}>
          <option value="">Toutes les priorités</option>
          {PRIORITES.map(p => <option key={p.value} value={p.value}>P{p.value} — {p.label}</option>)}
        </select>
      </div>}

      {/* Légende priorité */}
      {groupBy !== 'dashboard' && (
        <div className="si-prio-legend">
          <span className="si-prio-legend-label">Priorité :</span>
          {PRIORITES.map(p => (
            <span key={p.value} className="si-prio-legend-item" style={{ color: p.color, borderColor: p.color, background: p.bg }}>
              P{p.value} — {p.label}
            </span>
          ))}
        </div>
      )}

      {/* Dashboard */}
      {groupBy === 'dashboard' && (
        <div className="si-dashboard">
          {!dashData ? (
            <div className="si-empty-state"><div className="si-empty-icon">⏳</div><div className="si-empty-text">Chargement…</div></div>
          ) : (
            <>
              <div className="si-kpi-grid">
                {[
                  { key: 'toValidate',        icon: '⏳', label: 'À valider',            sub: 'en attente de validation', value: dashData.kpis.toValidate,        tone: 'decision' },
                  { key: 'validatedThisWeek', icon: '✅', label: 'Validés cette semaine', sub: 'depuis lundi',             value: dashData.kpis.validatedThisWeek, tone: 'success' },
                  { key: 'inProgress',        icon: '🔧', label: 'En cours',             sub: 'en traitement',            value: dashData.kpis.inProgress,        tone: 'warning' },
                  { key: 'blockingOpen',      icon: '🚨', label: 'Bloquants ouverts',    sub: 'priorité P3 non close',    value: dashData.kpis.blockingOpen,      tone: 'error' },
                ].map(k => (
                  <div key={k.key} className={`si-kpi-card si-kpi-card--${k.tone}`}>
                    <div className="si-kpi-head">
                      <span className="si-kpi-icon" aria-hidden="true">{k.icon}</span>
                      <span className="si-kpi-value">{k.value}</span>
                    </div>
                    <div className="si-kpi-label">{k.label}</div>
                    <div className="si-kpi-sub">{k.sub}</div>
                  </div>
                ))}
              </div>

              <div className="si-dash-grid">
                {/* Par statut */}
                <div className="si-dash-card">
                  <div className="si-dash-card-title">Par statut</div>
                  {STATUTS_VISIBLE.map(s => {
                    const count = dashData.byStatut[s.value] || 0;
                    const pct = dashData.total ? Math.round((count / dashData.total) * 100) : 0;
                    return (
                      <div key={s.value} className="si-dash-row">
                        <div className="si-dash-row-label">
                          <span className="si-stat-dot" style={{ background: s.color }} />
                          {s.label}
                        </div>
                        <div className="si-dash-bar-wrap">
                          <div className="si-dash-bar" style={{ width: `${pct}%`, background: s.color }} />
                        </div>
                        <span className="si-dash-row-count">{count}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Par type */}
                <div className="si-dash-card">
                  <div className="si-dash-card-title">Par type</div>
                  {[
                    { key: 'support',   ...TYPE_LABELS.support },
                    { key: 'evolution', ...TYPE_LABELS.evolution },
                  ].map(t => {
                    const count = dashData.byType[t.key] || 0;
                    const pct = dashData.total ? Math.round((count / dashData.total) * 100) : 0;
                    return (
                      <div key={t.key} className="si-dash-row">
                        <div className="si-dash-row-label">
                          <span style={{ marginRight: 4 }}>{t.icon}</span>
                          {t.label}
                        </div>
                        <div className="si-dash-bar-wrap">
                          <div className="si-dash-bar" style={{ width: `${pct}%`, background: t.color }} />
                        </div>
                        <span className="si-dash-row-count">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Contenu liste (masqué en mode dashboard) */}
      {groupBy !== 'dashboard' && loading && (
        <div className="si-empty-state">
          <div className="si-empty-icon">⏳</div>
          <div className="si-empty-text">Chargement…</div>
        </div>
      )}

      {groupBy === 'page' && !loading && demandes.length === 0 && (
        <div className="si-empty-state">
          <div className="si-empty-icon">🎉</div>
          <div className="si-empty-title">Aucune demande</div>
          {!isSuperAdmin && (
            <div className="si-empty-text">Cliquez sur « + Nouvelle demande » pour signaler un bug ou proposer une amélioration.</div>
          )}
        </div>
      )}

      {/* Kanban / Liste — Par statut */}
      {groupBy === 'statut' && !loading && (
        <>
          {/* Toggle vue */}
          <div className="si-view-toggle">
            <button
              className={`si-view-btn${viewMode === 'kanban' ? ' active' : ''}`}
              onClick={() => setViewMode('kanban')}
              title="Vue Kanban"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="5" height="18" rx="1"/><rect x="10" y="3" width="5" height="18" rx="1"/><rect x="17" y="3" width="4" height="18" rx="1"/></svg>
              Kanban
            </button>
            <button
              className={`si-view-btn${viewMode === 'list' ? ' active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Vue liste"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="3" cy="6" r="1" fill="currentColor"/><circle cx="3" cy="12" r="1" fill="currentColor"/><circle cx="3" cy="18" r="1" fill="currentColor"/></svg>
              Liste
            </button>
          </div>

          {/* Vue Kanban */}
          {viewMode === 'kanban' && (
            <div className="si-kanban">
              {STATUTS_VISIBLE.map(s => {
                const block = blocks.find(b => b.key === s.value);
                const list = block?.list || [];
                const isOver = dragOverCol === s.value;
                const collapsed = collapsedCols.has(s.value);
                return (
                  <div
                    key={s.value}
                    className={`si-kanban-col${isOver && !collapsed ? ' drag-over' : ''}${collapsed ? ' collapsed' : ''}`}
                    onDragOver={isSuperAdmin && !collapsed ? (e) => { e.preventDefault(); setDragOverCol(s.value); } : undefined}
                    onDragLeave={isSuperAdmin && !collapsed ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); } : undefined}
                    onDrop={isSuperAdmin && !collapsed ? (e) => { e.preventDefault(); handleDrop(s.value); } : undefined}
                  >
                    <div className="si-kanban-col-head" onClick={() => toggleColCollapse(s.value)} title={collapsed ? 'Déplier' : 'Replier'}>
                      <span className="si-status-dot" style={{ background: s.color }} />
                      <span className="si-status-label" style={{ color: texteTheme(s.color) }}>{s.label}</span>
                      <span className="si-status-count">{list.length}</span>
                      {!collapsed && (
                        <span className="si-col-collapse-icon">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                        </span>
                      )}
                    </div>
                    {!collapsed && (
                      <div className="si-kanban-cards">
                        {list.map(d => (
                          <TicketCard
                            key={d.id}
                            d={d}
                            byPage={false}
                            isSuperAdmin={isSuperAdmin}
                            onOpen={openTicket}
                            onDelete={handleDelete}
                            editMode={editMode}
                            onDragStart={isSuperAdmin ? handleDragStart : undefined}
                            onDragEnd={isSuperAdmin ? handleDragEnd : undefined}
                            isDragging={draggingCard === d.id}
                          />
                        ))}
                        {list.length === 0 && (
                          <div className="si-kanban-empty">
                            {draggingCard ? 'Déposer ici' : 'Aucune demande'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Vue liste groupée par statut */}
          {viewMode === 'list' && (
            <div className="si-status-list">
              {STATUTS_VISIBLE.map(s => {
                const block = blocks.find(b => b.key === s.value);
                const list = block?.list || [];
                if (!list.length) return null;
                return (
                  <div className="si-status-block" key={s.value}>
                    <div className="si-status-head" style={{ borderColor: s.color }}>
                      <span className="si-status-dot" style={{ background: s.color }} />
                      <span className="si-status-label" style={{ color: texteTheme(s.color) }}>{s.label}</span>
                      <span className="si-status-count">{list.length}</span>
                    </div>
                    <div className="si-cards">
                      {list.map(d => (
                        <TicketCard
                          key={d.id}
                          d={d}
                          byPage={false}
                          isSuperAdmin={isSuperAdmin}
                          onOpen={openTicket}
                          onDelete={handleDelete}
                          editMode={editMode}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Modale attribution — nouveau → en cours */}
      {assignModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Qui prend ce ticket ?</h2>
              <button className="modal-close" onClick={() => setAssignModal(null)}>✕</button>
            </div>
            <div className="modal-body si-assign-body">
              {ASSIGNEES.map(a => (
                <button
                  key={a.key}
                  className="si-assign-btn"
                  style={{ borderColor: a.color, color: texteTheme(a.color) }}
                  onClick={() => handleAssign(a.key)}
                >
                  <span className="si-assign-initials" style={{ background: a.color }}>{a.key}</span>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Liste verticale — Par page concernée */}
      {groupBy === 'page' && !loading && demandes.length > 0 && (
        <div className="si-status-list">
          {blocks.map(b => {
            if (!b.list?.length) return null;
            return (
              <div className="si-status-block" key={b.key}>
                <div className="si-status-head" style={{ borderColor: b.color }}>
                  <span className="si-status-dot" style={{ background: b.color }} />
                  <span className="si-status-label" style={{ color: texteTheme(b.color) }}>{b.label}</span>
                  <span className="si-status-count">{b.list.length}</span>
                  {isSuperAdmin && (
                    <button
                      className="si-prompt-btn"
                      onClick={() => openGroupPrompt(b)}
                      title={`Générer un prompt regroupant ${b.list.length} demande(s) pour Claude Code`}
                    >
                      🤖 Prompt IA
                    </button>
                  )}
                </div>
                <div className="si-cards">
                  {b.list.map(d => (
                    <TicketCard
                      key={d.id}
                      d={d}
                      byPage={true}
                      isSuperAdmin={isSuperAdmin}
                      onOpen={openTicket}
                      onDelete={handleDelete}
                      editMode={editMode}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modale aperçu prompt IA */}
      {promptModal && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🤖 Prompt généré — {promptModal.label}</h2>
              {!promptModal.loading && <button className="modal-close" onClick={() => setPromptModal(null)}>✕</button>}
            </div>
            <div className="modal-body">
              {promptModal.loading ? (
                <div className="si-empty-state">
                  <div className="si-empty-icon">⏳</div>
                  <div className="si-empty-text">Génération du prompt par l'IA…</div>
                </div>
              ) : (
                <>
                  {promptModal.fallback && (
                    <p className="si-hint" style={{ color: 'light-dark(var(--color-warning), var(--color-warning-dark))' }}>
                      ⚠️ IA indisponible — prompt brut (titres + descriptions non reformulés).
                    </p>
                  )}
                  <p className="si-hint">✏️ Vous pouvez modifier le prompt avant de le copier.</p>
                  <textarea
                    className="si-prompt-textarea"
                    value={promptModal.text}
                    onChange={e => setPromptModal(m => (m ? { ...m, text: e.target.value, copied: false } : m))}
                    spellCheck={false}
                  />
                </>
              )}
            </div>
            {!promptModal.loading && (
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setPromptModal(null)}>Fermer</button>
                <button className="btn-primary" onClick={copyModalText}>
                  {promptModal.copied ? '✓ Copié' : '📋 Copier'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modale création */}
      {showCreate && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouvelle demande</h2>
              <button className="modal-close" onClick={() => setShowCreate(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="si-field">
                <label>Type de demande *</label>
                <div className="si-type-cards">
                  <button
                    className={`si-type-card${form.type === 'support' ? ' active bug' : ''}`}
                    onClick={() => setForm(f => ({ ...f, type: 'support' }))}
                  >
                    <div className="si-type-card-icon">🐛</div>
                    <div className="si-type-card-name">Bug</div>
                    <div className="si-type-card-desc">Un problème, une erreur ou un dysfonctionnement</div>
                  </button>
                  <button
                    className={`si-type-card${form.type === 'evolution' ? ' active evolution' : ''}`}
                    onClick={() => setForm(f => ({ ...f, type: 'evolution' }))}
                  >
                    <div className="si-type-card-icon">✨</div>
                    <div className="si-type-card-name">Amélioration</div>
                    <div className="si-type-card-desc">Une idée, une évolution ou une nouvelle fonctionnalité</div>
                  </button>
                </div>
              </div>

              <div className="si-field">
                <label>Priorité *</label>
                <div className="si-prio-cards">
                  {PRIORITES.map(p => (
                    <button
                      key={p.value}
                      type="button"
                      className={`si-prio-card${form.priorite === p.value ? ' active' : ''}`}
                      style={form.priorite === p.value ? { borderColor: p.color, color: p.color, background: p.bg } : {}}
                      onClick={() => setForm(f => ({ ...f, priorite: p.value }))}
                    >
                      <span className="si-prio-card-num">P{p.value}</span>
                      <span className="si-prio-card-label">{p.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="si-field">
                <label>Titre *</label>
                <input
                  value={form.titre}
                  onChange={e => setForm(f => ({ ...f, titre: e.target.value }))}
                  placeholder="Résumez votre demande en une ligne"
                  maxLength={255}
                />
              </div>

              <div className="si-field">
                <label>Page concernée</label>
                <select value={form.page} onChange={e => setForm(f => ({ ...f, page: e.target.value }))}>
                  <option value="">— Aucune / général —</option>
                  {[...accessiblePages].sort((a, b) => {
                    const label = s => s.nom.slice(s.nom.indexOf(' ') + 1);
                    return label(a).localeCompare(label(b), 'fr');
                  }).map(p => (
                    <option key={p.id} value={p.id}>{p.nom}</option>
                  ))}
                </select>
                <p className="si-hint">Vous ne voyez que les pages auxquelles vous avez accès.</p>
              </div>

              <div className="si-field">
                <label>Description</label>
                <textarea
                  rows={5}
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Décrivez votre demande en détail : contexte, comportement observé, résultat attendu…"
                />
              </div>

              <div className="si-field">
                <label>Assigné à</label>
                {isSuperAdmin ? (
                  <div className="si-assignee-choice">
                    {ASSIGNEES.map(a => {
                      const isActive = form.assigned_to === a.key;
                      return (
                        <button
                          key={a.key}
                          type="button"
                          className={`si-assignee-btn${isActive ? ' active' : ''}`}
                          style={isActive ? { borderColor: a.color, color: texteTheme(a.color) } : {}}
                          onClick={() => setForm(f => ({ ...f, assigned_to: isActive ? '' : a.key }))}
                          title={isActive ? 'Cliquer pour désassigner' : `Assigner à ${a.label}`}
                        >
                          <span className="si-assignee-initial" style={{ background: a.color }}>{a.key}</span>
                          {a.label}
                        </button>
                      );
                    })}
                    {!form.assigned_to && <span className="si-hint" style={{ alignSelf: 'center' }}>Non assigné</span>}
                  </div>
                ) : (
                  <p className="si-hint">L'assignation est gérée par l'équipe.</p>
                )}
              </div>

              <DocumentSection
                documents={newDocuments}
                setDocuments={setNewDocuments}
                parentId={null}
                docsApi={supportInnovationDocsApi}
              />
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreate(false)} disabled={saving}>Annuler</button>
              <button className="btn-primary" onClick={handleCreate} disabled={saving}>
                {saving ? 'Envoi…' : 'Créer la demande'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale détail */}
      {detailId && (
        <DemandeDetail
          id={detailId}
          isSuperAdmin={isSuperAdmin}
          isAdmin={isAdmin}
          onClose={closeTicket}
          onChanged={loadDemandes}
          onOpenTicket={openTicket}
          onCreateLinked={isSuperAdmin ? () => {
            const masterTicketId = detailId;
            setLinkedContext(masterTicketId);
            closeTicket();
            setForm({ type: 'support', titre: '', description: '', page: '', priorite: '', assigned_to: '' });
            setNewDocuments([]);
            setShowCreate(true);
          } : undefined}
        />
      )}
    </div>
  );
}

// ─── Détail d'une demande + fil de suivi ─────────────────────────────────────
function DemandeDetail({ id, isSuperAdmin, isAdmin = false, onClose, onChanged, onOpenTicket, onCreateLinked }) {
  const [demande, setDemande]   = useState(null);
  const [commentaires, setCommentaires] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting]   = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText]   = useState('');
  const [savingComment, setSavingComment]        = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [copied, setCopied]     = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [editingContent, setEditingContent] = useState(false);
  const [editForm, setEditForm] = useState({ titre: '', description: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  // Sous-tickets (hiérarchie parent/enfant)
  const [children, setChildren] = useState([]);
  const [showAttachSearch, setShowAttachSearch] = useState(false);
  const [attachQuery, setAttachQuery] = useState('');
  const [allDemandes, setAllDemandes] = useState([]);
  const [loadingAllDemandes, setLoadingAllDemandes] = useState(false);
  // Recherche "Rattacher un ticket" : résultats groupés par statut (mêmes groupes que la liste enfants)
  const [attachGroupsCollapsed, setAttachGroupsCollapsed] = useState(
    Object.fromEntries(SUB_TICKET_GROUPS.map(g => [g.key, g.collapsed]))
  );
  // Liste des sous-tickets rattachés : état de repliage par groupe
  const [childGroupsCollapsed, setChildGroupsCollapsed] = useState(
    Object.fromEntries(SUB_TICKET_GROUPS.map(g => [g.key, g.collapsed]))
  );
  // Recherche dans la liste des sous-tickets rattachés
  const [childSearchQuery, setChildSearchQuery] = useState('');

  // Commits déployés
  const [commitRefs, setCommitRefs] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c, docs, crefs] = await Promise.all([
        supportInnovationApi.getById(id),
        supportInnovationApi.getCommentaires(id),
        supportInnovationDocsApi.getAll(id).catch(() => ({ data: { documents: [] } })),
        supportInnovationApi.getCommitRefs(id).catch(() => ({ data: { refs: [] } })),
      ]);
      const dem = d.data.demande;
      setDemande(dem);
      setChildren(dem.children || []);
      setCommentaires(c.data.commentaires || []);
      setDocuments((docs.data.documents || []).map(x => normalizeDoc({ ...x, isLocal: false })));
      setCommitRefs(crefs.data.refs || []);
    } catch (err) {
      console.error('[Support] détail:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleStatut = async (statut) => {
    try {
      await supportInnovationApi.update(id, { statut });
      await load();
      onChanged && onChanged();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await supportInnovationApi.addCommentaire(id, newComment.trim());
      setNewComment('');
      await load();
      onChanged && onChanged();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setPosting(false);
    }
  };

  const handleCommentPaste = useCallback(async (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItems = items.filter(item => item.kind === 'file' && item.type.startsWith('image/'));
    if (imageItems.length === 0) return;
    e.preventDefault();
    for (const item of imageItems) {
      const file = item.getAsFile();
      if (!file) continue;
      const ext = file.type.split('/')[1] || 'png';
      const namedFile = new File([file], `capture-${Date.now()}.${ext}`, { type: file.type });
      const tempId = Date.now() + Math.random();
      const tempDoc = { id: tempId, name: namedFile.name, type: namedFile.type, url: URL.createObjectURL(namedFile), isLocal: true, uploading: true };
      setDocuments(prev => [...prev, tempDoc]);
      try {
        const compressed = await compressFile(namedFile);
        const presignRes = await supportInnovationDocsApi.presign(id, { filename: compressed.name, contentType: compressed.type });
        await fetch(presignRes.data.uploadUrl, { method: 'PUT', body: compressed, headers: { 'Content-Type': compressed.type } });
        const confirmRes = await supportInnovationDocsApi.confirm(id, { key: presignRes.data.key, filename: compressed.name, contentType: compressed.type, size: compressed.size });
        URL.revokeObjectURL(tempDoc.url);
        setDocuments(prev => prev.map(d => d.id === tempId ? { ...normalizeDoc(confirmRes.data.document), isLocal: false } : d));
      } catch (err) {
        console.error('Erreur upload capture:', err);
        setDocuments(prev => { const d = prev.find(x => x.id === tempId); if (d?.url?.startsWith('blob:')) URL.revokeObjectURL(d.url); return prev.filter(x => x.id !== tempId); });
      }
    }
  }, [id, setDocuments]);

  const handleUpdateComment = async (commentId) => {
    if (!editCommentText.trim()) return;
    setSavingComment(true);
    try {
      await supportInnovationApi.updateCommentaire(id, commentId, editCommentText.trim());
      setEditingCommentId(null);
      setEditCommentText('');
      await load();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingComment(false);
    }
  };

  const handleGeneratePrompt = async () => {
    if (!demande) return;
    setGenLoading(true);
    try {
      const r = await supportInnovationApi.generatePrompt(id, demande.page ? pageLabel(demande.page) : '');
      setDemande(prev => ({ ...prev, prompt_genere: r.data.prompt }));
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setGenLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      const url = `${window.location.origin}${window.location.pathname}?page=support-innovation&ticket=${id}`;
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch { /* clipboard indisponible */ }
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(demande?.prompt_genere || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard indisponible */ }
  };

  const handleDateLivraison = async (value) => {
    try {
      const r = await supportInnovationApi.update(id, { date_livraison_prevue: value || null });
      setDemande(prev => ({ ...prev, date_livraison_prevue: r.data.demande.date_livraison_prevue }));
      onChanged && onChanged();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAssignedTo = async (key) => {
    const current = (demande.assigned_to || '').split(',').filter(Boolean);
    const next = current.includes(key)
      ? current.filter(k => k !== key)
      : [...current, key];
    const val = next.join(',') || null;
    try {
      await supportInnovationApi.update(id, { assigned_to: val });
      setDemande(prev => ({ ...prev, assigned_to: val }));
      onChanged && onChanged();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const handlePriorite = async (value) => {
    try {
      await supportInnovationApi.update(id, { priorite: value });
      setDemande(prev => ({ ...prev, priorite: value }));
      onChanged && onChanged();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSaveContent = async () => {
    if (!editForm.titre.trim()) { alert('Le titre est requis'); return; }
    setSavingEdit(true);
    try {
      await supportInnovationApi.update(id, { titre: editForm.titre, description: editForm.description });
      setEditingContent(false);
      await load();
      onChanged && onChanged();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAttachSubticket = async (targetId) => {
    try {
      await supportInnovationApi.setParent(targetId, id);
      setShowAttachSearch(false);
      setAttachQuery('');
      await load();
      onChanged && onChanged();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDetachChild = async (childId) => {
    if (!window.confirm('Retirer ce sous-ticket ? Il redeviendra un ticket autonome.')) return;
    try {
      await supportInnovationApi.setParent(childId, null);
      setChildren(prev => prev.filter(c => c.id !== childId));
      onChanged && onChanged();
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    }
  };

  const openAttachSearch = async () => {
    if (!showAttachSearch) {
      setLoadingAllDemandes(true);
      try {
        const r = await supportInnovationApi.getAll({});
        setAllDemandes(r.data.demandes || []);
      } catch (_) {}
      setLoadingAllDemandes(false);
    }
    setShowAttachSearch(s => !s);
    setAttachQuery('');
  };

  const si = demande ? statutInfo(demande.statut) : null;
  const type = demande ? (TYPE_LABELS[demande.type] || { label: demande.type, icon: '•', color: 'var(--text-muted)', bg: 'var(--bg-muted)' }) : null;
  const canAuthorEdit = !isSuperAdmin && !isAdmin && demande && ['nouveau', 'a_valider'].includes(demande.statut);
  // Super-admin et admin peuvent éditer titre/description à tout moment (cf. backend canManage)
  const canManageEdit = isSuperAdmin || isAdmin;

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="si-modal-title-wrap">
            {demande?.parent_info && (
              <div
                className="si-parent-breadcrumb"
                onClick={() => onOpenTicket && onOpenTicket(demande.parent_info.id)}
                title={`Ouvrir le ticket Master : ${demande.parent_info.titre}`}
              >
                ↳ Sous-ticket de {ticketNum(demande.parent_info.ticket_number)} {demande.parent_info.titre}
              </div>
            )}
            <h2>
              {demande?.ticket_number && <span className="si-ticket-num si-ticket-num--header">{ticketNum(demande.ticket_number)}</span>}
              {loading ? 'Chargement…' : (demande?.titre || '')}
            </h2>
          </div>
          {demande?.ticket_number && (
            <button className="btn-secondary btn-sm" onClick={handleCopyLink} title="Copier le lien vers ce ticket">
              {linkCopied ? 'Copié ✓' : '🔗 Copier le lien'}
            </button>
          )}
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {loading || !demande ? (
            <div className="si-empty-state" style={{ padding: 40 }}>
              <div className="si-empty-icon">⏳</div>
              <div className="si-empty-text">Chargement…</div>
            </div>
          ) : (
            <>
              {/* Méta */}
              <div className="si-detail-meta">
                {type && (
                  <span className="si-card-type-badge" style={{ color: type.color, background: type.bg }}>
                    {type.icon} {type.label}
                  </span>
                )}
                {si && (
                  <span className="si-badge" style={{ color: texteTheme(si.color), borderColor: si.color }}>{si.label}</span>
                )}
                {isSuperAdmin && demande.auteur_nom && (
                  <span className="si-meta-text">Par {demande.auteur_prenom} {demande.auteur_nom}{demande.concession_nom ? ` · ${demande.concession_nom}` : ''}</span>
                )}
                <span className="si-meta-text">Créée le {formatDate(demande.created_at)}</span>
                {demande.page && <span className="si-meta-text">Page : {pageLabel(demande.page)}</span>}
                {demande.date_livraison_prevue && (
                  <span className="si-meta-livraison">📅 Livraison prévue : {formatDate(demande.date_livraison_prevue)}</span>
                )}
                {demande.modifier_prenom && (
                  <span className="si-meta-text">Modifié par {demande.modifier_prenom} {demande.modifier_nom} le {formatDate(demande.last_modified_at)}</span>
                )}
              </div>

              {/* Contenu éditable */}
              {editingContent ? (
                <div className="si-field">
                  <label>Titre *</label>
                  <input value={editForm.titre} onChange={e => setEditForm(f => ({ ...f, titre: e.target.value }))} maxLength={255} />
                  <label style={{ marginTop: 12 }}>Description</label>
                  <textarea rows={5} value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button className="btn-secondary" onClick={() => setEditingContent(false)} disabled={savingEdit}>Annuler</button>
                    <button className="btn-primary" onClick={handleSaveContent} disabled={savingEdit}>{savingEdit ? 'Enregistrement…' : 'Enregistrer'}</button>
                  </div>
                </div>
              ) : (
                <>
                  {demande.description && <p className="si-description">{demande.description}</p>}
                  {(canAuthorEdit || canManageEdit) && (
                    <button
                      className="btn-secondary btn-sm"
                      style={{ marginTop: 8 }}
                      onClick={() => { setEditForm({ titre: demande.titre, description: demande.description || '' }); setEditingContent(true); }}
                    >
                      ✏️ {canManageEdit ? 'Modifier le ticket' : 'Modifier ma demande'}
                    </button>
                  )}
                </>
              )}

              <DocumentSection
                documents={documents}
                setDocuments={setDocuments}
                parentId={id}
                docsApi={supportInnovationDocsApi}
              />

              {/* Avancement (super-admin) */}
              {isSuperAdmin && (
                <div className="si-field" style={{ marginTop: 16 }}>
                  <label>Faire avancer</label>
                  <div className="si-statut-choice">
                    {STATUTS_VISIBLE.map(s => (
                      <button
                        key={s.value}
                        className={demande.statut === s.value ? 'active' : ''}
                        style={demande.statut === s.value
                          ? { color: '#fff', background: s.color, borderColor: s.color }
                          : { color: texteTheme(s.color), borderColor: s.color }}
                        onClick={() => handleStatut(s.value)}
                      >{s.label}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Priorité (super-admin) */}
              {isSuperAdmin && (
                <div className="si-field" style={{ marginTop: 16 }}>
                  <label>Priorité</label>
                  <div className="si-statut-choice">
                    {PRIORITES.map(p => (
                      <button
                        key={p.value}
                        className={demande.priorite === p.value ? 'active' : ''}
                        style={demande.priorite === p.value
                          ? { color: '#fff', background: p.color, borderColor: p.color }
                          : { color: texteTheme(p.color), borderColor: p.color }}
                        onClick={() => handlePriorite(p.value)}
                      >P{p.value} {p.label}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Attribution (super-admin) */}
              {isSuperAdmin && (
                <div className="si-field" style={{ marginTop: 16 }}>
                  <label>Assigné à</label>
                  <div className="si-assignee-choice">
                    {ASSIGNEES.map(a => {
                      const isActive = (demande.assigned_to || '').split(',').includes(a.key);
                      return (
                        <button
                          key={a.key}
                          className={`si-assignee-btn${isActive ? ' active' : ''}`}
                          style={isActive ? { borderColor: a.color, color: texteTheme(a.color) } : {}}
                          onClick={() => handleAssignedTo(a.key)}
                          title={isActive ? 'Cliquer pour désassigner' : `Assigner à ${a.label}`}
                        >
                          <span className="si-assignee-initial" style={{ background: a.color }}>
                            {a.key}
                          </span>
                          {a.label}
                        </button>
                      );
                    })}
                    {!demande.assigned_to && <span className="si-hint" style={{ alignSelf: 'center' }}>Non assigné</span>}
                  </div>
                </div>
              )}

              {/* Date de livraison (super-admin) */}
              {isSuperAdmin && (
                <div className="si-field" style={{ marginTop: 12 }}>
                  <label>Date de livraison prévisionnelle</label>
                  <input
                    type="date"
                    value={demande.date_livraison_prevue ? String(demande.date_livraison_prevue).slice(0, 10) : ''}
                    onChange={e => handleDateLivraison(e.target.value)}
                    style={{ maxWidth: 220 }}
                  />
                  <p className="si-hint">Renseignée par le super-admin, visible par tous. Laisser vide pour effacer.</p>
                </div>
              )}

              {/* Prompt Claude Code (super-admin, améliorations) */}
              {isSuperAdmin && demande.type === 'evolution' && (
                <div className="si-prompt-block">
                  <div className="si-prompt-head">
                    <h4>🧩 Prompt Claude Code</h4>
                    <div className="si-prompt-actions">
                      {demande.prompt_genere && (
                        <button className="btn-secondary btn-sm" onClick={handleCopyPrompt}>{copied ? 'Copié ✓' : 'Copier'}</button>
                      )}
                      <button className="btn-primary btn-sm" onClick={handleGeneratePrompt} disabled={genLoading}>
                        {genLoading ? 'Génération…' : (demande.prompt_genere ? 'Régénérer' : 'Générer le prompt')}
                      </button>
                    </div>
                  </div>
                  {demande.prompt_genere ? (
                    <pre className="si-prompt-text">{demande.prompt_genere}</pre>
                  ) : (
                    <p className="si-meta-text">Génère, via Claude, un prompt prêt à coller dans Claude Code pour implémenter cette amélioration plus vite.</p>
                  )}
                </div>
              )}

              {/* Fil de discussion */}
              <div className="si-thread">
                <h4>Suivi de la demande</h4>
                {commentaires.length === 0 && <p className="si-meta-text">Aucun échange pour le moment.</p>}
                {commentaires.map(c => (
                  <div key={c.id} className={`si-comment${c.role === 'super-admin' ? ' admin' : ''}`}>
                    <div className="si-comment-head">
                      <strong>{c.prenom} {c.nom}{c.role === 'super-admin' ? ' · Équipe' : ''}</strong>
                      <span>{new Date(c.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    {editingCommentId === c.id ? (
                      <div className="si-comment-edit">
                        <textarea rows={2} value={editCommentText} onChange={e => setEditCommentText(e.target.value)} />
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                          <button className="btn-secondary btn-sm" onClick={() => { setEditingCommentId(null); setEditCommentText(''); }} disabled={savingComment}>Annuler</button>
                          <button className="btn-primary btn-sm" onClick={() => handleUpdateComment(c.id)} disabled={savingComment || !editCommentText.trim()}>{savingComment ? '…' : 'Enregistrer'}</button>
                        </div>
                      </div>
                    ) : (
                      <div className="si-comment-body">
                        {c.commentaire}
                        {canManageEdit && (
                          <button
                            className="si-comment-edit-btn"
                            title="Modifier le message"
                            onClick={() => { setEditingCommentId(c.id); setEditCommentText(c.commentaire); }}
                          >✏️</button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <div className="si-comment-form">
                  <textarea
                    rows={2}
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    onPaste={handleCommentPaste}
                    placeholder="Ajouter un message… (Ctrl+V pour coller une capture d'écran)"
                  />
                  <button className="btn-primary" onClick={handleComment} disabled={posting || !newComment.trim()}>
                    {posting ? '…' : 'Envoyer'}
                  </button>
                </div>
              </div>

              {/* Sous-tickets */}
              {isSuperAdmin && (
                <div className="si-linked-section">
                  <div className="si-linked-header">
                    <h4>
                      📋 Sous-tickets
                      {children.length > 0 && (
                        <span className="si-linked-count">{children.filter(c => ['valide','resolu'].includes(c.statut)).length}/{children.length}</span>
                      )}
                    </h4>
                    {children.length > 0 && (
                      <input
                        className="si-child-search-input"
                        placeholder="🔍 Filtrer par nom ou #id…"
                        value={childSearchQuery}
                        onChange={e => setChildSearchQuery(e.target.value)}
                      />
                    )}
                    <div className="si-linked-actions">
                      <button className="btn-secondary btn-sm" onClick={openAttachSearch}>
                        {showAttachSearch ? 'Annuler' : '+ Rattacher un ticket'}
                      </button>
                      {onCreateLinked && (
                        <button className="btn-secondary btn-sm" onClick={onCreateLinked}>
                          + Créer un sous-ticket
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Barre de progression */}
                  {children.length > 0 && (() => {
                    const done = children.filter(c => ['valide','resolu'].includes(c.statut)).length;
                    const pct = Math.round((done / children.length) * 100);
                    return (
                      <div className="si-children-modal-progress">
                        <div className="si-children-modal-bar">
                          <div className="si-children-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="si-children-modal-pct">{pct}%</span>
                      </div>
                    );
                  })()}

                  {/* Recherche pour rattacher un ticket existant */}
                  {showAttachSearch && (
                    <div className="si-link-search">
                      <input
                        className="si-link-search-input"
                        placeholder="🔍 Rechercher par titre ou #numéro…"
                        value={attachQuery}
                        onChange={e => setAttachQuery(e.target.value)}
                        autoFocus
                      />
                      {loadingAllDemandes ? (
                        <p className="si-meta-text">Chargement…</p>
                      ) : (
                        <div className="si-link-results">
                          {(() => {
                            const filtered = allDemandes.filter(d => {
                              if (d.id === id) return false;
                              if (d.parent_ticket_id) return false; // déjà sous-ticket d'un autre
                              if (children.some(c => c.id === d.id)) return false;
                              if (!attachQuery.trim()) return true;
                              const q = attachQuery.toLowerCase().replace('#', '');
                              const pn = String(d.ticket_number || '').padStart(5, '0');
                              return (d.titre || '').toLowerCase().includes(q) || pn.includes(q) || String(d.ticket_number || '').includes(q);
                            });
                            if (!filtered.length) return <p className="si-meta-text" style={{ padding: '8px 12px' }}>Aucun ticket autonome trouvé.</p>;
                            const renderResult = (d) => {
                              const ts = statutInfo(d.statut);
                              const tp = PRIORITES.find(p => p.value === String(d.priorite));
                              return (
                                <div key={d.id} className="si-link-result-item" onClick={() => handleAttachSubticket(d.id)} role="button">
                                  <span className="si-ticket-num">{ticketNum(d.ticket_number)}</span>
                                  {tp && <span style={{ color: texteTheme(tp.color), fontSize: '0.7rem', fontWeight: 700 }}>P{d.priorite}</span>}
                                  <span className="si-link-result-titre">{d.titre}</span>
                                  <span className="si-badge" style={{ color: texteTheme(ts.color), borderColor: ts.color, fontSize: '0.7rem' }}>{ts.label}</span>
                                </div>
                              );
                            };
                            // Résultats groupés par statut : "En cours" déplié par défaut,
                            // "À valider / clos" replié par défaut (cf. SUB_TICKET_GROUPS).
                            return SUB_TICKET_GROUPS.map(group => {
                              const items = filtered.filter(d => group.statuts.includes(d.statut));
                              if (!items.length) return null;
                              const collapsed = attachGroupsCollapsed[group.key];
                              return (
                                <div key={group.key} className="si-linked-subgroup">
                                  <button
                                    type="button"
                                    className="si-linked-subgroup-head"
                                    onClick={() => setAttachGroupsCollapsed(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
                                  >
                                    <span className={`si-col-collapse-icon${collapsed ? ' si-collapsed' : ''}`}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                                    </span>
                                    <span>{group.label}</span>
                                    <span className="si-linked-count">{items.length}</span>
                                  </button>
                                  {!collapsed && items.map(renderResult)}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Liste des sous-tickets — groupés par statut */}
                  {children.length === 0 ? (
                    <p className="si-meta-text">Aucun sous-ticket rattaché.</p>
                  ) : (
                    <div className="si-linked-list">
                      {SUB_TICKET_GROUPS.map(group => {
                        const childQ = childSearchQuery.toLowerCase().replace('#', '');
                        const filteredChildren = childQ
                          ? children.filter(t => {
                              const pn = String(t.ticket_number || '').padStart(5, '0');
                              return (t.titre || '').toLowerCase().includes(childQ) || pn.includes(childQ) || String(t.ticket_number || '').includes(childQ);
                            })
                          : children;
                        const items = filteredChildren.filter(t => group.statuts.includes(t.statut));
                        if (!items.length) return null;
                        const collapsed = childGroupsCollapsed[group.key];
                        return (
                          <div key={group.key} className="si-linked-subgroup">
                            <button
                              type="button"
                              className="si-linked-subgroup-head"
                              onClick={() => setChildGroupsCollapsed(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
                            >
                              <span className={`si-col-collapse-icon${collapsed ? ' si-collapsed' : ''}`}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                              </span>
                              <span>{group.label}</span>
                              <span className="si-linked-count">{items.length}</span>
                            </button>
                            {!collapsed && items.map(t => {
                              const ts = statutInfo(t.statut);
                              const tp = PRIORITES.find(p => p.value === String(t.priorite));
                              const taKeys = t.assigned_to ? t.assigned_to.split(',').filter(Boolean) : [];
                              const tt = TYPE_LABELS[t.type];
                              return (
                                <div
                                  key={t.id}
                                  className="si-linked-item"
                                  onClick={() => onOpenTicket && onOpenTicket(t.id)}
                                  role="button"
                                  title="Ouvrir ce sous-ticket"
                                >
                                  <span className="si-ticket-num">{ticketNum(t.ticket_number)}</span>
                                  {tt && (
                                    <span className="si-card-type-badge" style={{ color: tt.color, background: tt.bg, fontSize: '0.7rem' }}>
                                      {tt.icon} {tt.label}
                                    </span>
                                  )}
                                  {tp && (
                                    <span className="si-card-prio-badge" style={{ color: tp.color, background: tp.bg }}>
                                      P{t.priorite}
                                    </span>
                                  )}
                                  <span className="si-linked-titre">{t.titre}</span>
                                  <span className="si-badge" style={{ color: texteTheme(ts.color), borderColor: ts.color, fontSize: '0.7rem' }}>{ts.label}</span>
                                  {taKeys.map(k => {
                                    const ta = ASSIGNEES.find(a => a.key === k);
                                    return ta ? <span key={k} className="si-assigned-badge" style={{ background: ta.color }} title={ta.label}>{ta.key}</span> : null;
                                  })}
                                  <button
                                    className="btn-icon"
                                    title="Retirer ce sous-ticket (devient ticket autonome)"
                                    onClick={e => { e.stopPropagation(); handleDetachChild(t.id); }}
                                    style={{ marginLeft: 'auto', flexShrink: 0 }}
                                  >✕</button>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Commits déployés */}
              {commitRefs.length > 0 && (
                <div className="si-deploy-section">
                  <h4>🚀 Déploiements ({commitRefs.length})</h4>
                  <div className="si-deploy-list">
                    {commitRefs.map(r => (
                      <div key={r.commit_hash} className="si-deploy-item">
                        <div className="si-deploy-top">
                          <span className="si-deploy-hash">{r.commit_hash.slice(0, 7)}</span>
                          {r.branch && <span className="si-deploy-branch">{r.branch}</span>}
                          <span className="si-deploy-date">{formatDate(r.deployed_at)}</span>
                        </div>
                        <span className="si-deploy-msg">{r.commit_message}</span>
                        {r.commit_author && <span className="si-deploy-meta">{r.commit_author}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
